
import { inject, Injectable, DOCUMENT } from '@angular/core';
import { environment } from 'environments/environment';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { translate } from '@jsverse/transloco';
import { Meta, Title } from '@angular/platform-browser';


interface PathLocalizationMapping {
  url: string;
  localizationKeys: {
    title: string;
    description: string;
  }
}

const INCLUDED_PATHS = new Set([
  "/",
  "/home",
  "/gledaj",
  "/live/mosaic",
  "/live/programme",
  "/videostore",
  "/my-content",
  "/account/login",
  "/account/registration",
  "/account/invitation",
  "/account/invitationSuccess",
  "/account/invitationFailure",
  "/configuration-center/settings/faq",
  "/configuration-center/settings/help",
]);

const PATH_LOCALIZATIONS: PathLocalizationMapping[] = [
  {
    url: "/",
    localizationKeys: {
      title: "about.app_name",
      description: "about.app_description"
    }
  },
  {
    url: "/home",
    localizationKeys: {
      title: "about.app_name",
      description: "about.app_description"
    }
  }
]

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly _document = inject(DOCUMENT);
  private readonly _router = inject(Router);
  private readonly _title = inject(Title);
  private readonly _meta = inject(Meta)


  public registerOnUrlEvents() {
    const domain = environment.operator.domains[0];
    if (!environment.enableSeo) {
      return;
    }
    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed()
    ).subscribe((event: NavigationEnd) => {
      const url = event.urlAfterRedirects;
      if (INCLUDED_PATHS.has(url)) {
        this.setCanonicalUrl(`https://${domain}${url}`);
        this.removeNoIndex();
      } else {
        this.setNoIndex();
      }

      const pathMapping = PATH_LOCALIZATIONS.find(pathMapping => pathMapping.url == url);
      if (pathMapping) {
        this.setMetaTags(pathMapping)
      }
      else {
        this.setMetaTags({
          url: null,
          localizationKeys: {
            title: 'about.app_name',
            description: null,
          }
        })
      }
    });
  }

  setMetaTags(pathMapping: PathLocalizationMapping) {
    this._title.setTitle(translate(pathMapping.localizationKeys.title));
    if (pathMapping.localizationKeys.description) {
      this._meta.updateTag({ name: "description", content: translate(pathMapping.localizationKeys.description) });
    }
  }

  setNoIndex() {
    this._meta.updateTag({ name: "robots", content: "noindex" });
  }

  removeNoIndex() {
    const tag = this._meta.getTag("name='robots'");
    if (tag) {
      this._meta.removeTagElement(tag);
    }
  }

  setCanonicalUrl(url: string) {
    let canonical = this._document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = this._document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      this._document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);
  }
}
